import type { ActionFields } from '@tribeplatform/gql-client/graphql'
import type {
  Action,
  MutationUpdateLocaleGroupArgs,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import {
  getCollectionKey,
  getCollectionsKey,
} from '../../utils/keys/collection.key.js'
import {
  getLocaleGroupKey,
  getLocaleGroupsKey,
} from '../../utils/keys/localeGroup.key.js'
import { getSpaceKey, getSpacesKey } from '../../utils/keys/space.key.js'

export const useUpdateLocaleGroup = (options?: {
  fields?: ActionFields
  useMutationOptions?: UseMutationOptions<
    Action,
    Error,
    MutationUpdateLocaleGroupArgs
  >
}) => {
  const { useMutationOptions, fields = 'basic' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<Action, Error, MutationUpdateLocaleGroupArgs>(
    (input: MutationUpdateLocaleGroupArgs) =>
      client.localeGroups.update(input, fields),
    {
      onSuccess: async (_action, { id }) => {
        queryClient.invalidateQueries(getLocaleGroupKey({ variables: { id } }))
        queryClient.invalidateQueries(getLocaleGroupsKey())
        // Editing the base path re-paths every member space (address/relativeUrl
        // change), so refresh space and collection queries too. The hook only has
        // the group id, hence the namespace-wide space invalidation.
        queryClient.invalidateQueries(getSpaceKey())
        queryClient.invalidateQueries(getSpacesKey())
        // Invalidate both the collections list and collection detail (generic,
        // no id): the update can move the group between collections, so both the
        // source and target detail caches (e.g. CollectionMenuProvider) restale.
        queryClient.invalidateQueries(getCollectionKey())
        queryClient.invalidateQueries(getCollectionsKey())
      },
      ...useMutationOptions,
    },
  )
}
